import {
  MOCK_COURSES,
  getDayById,
  type ICourse,
  type ICourseDay,
} from "@/data/courses";
import { getExerciseById, getAllExercises } from "@/data/exercises";
import { normalizeFreeLibraryDay, normalizeLibrarySelectionIds } from './libraryReferenceMapping';
import type {
  ITrainingPlan,
  ITrainingPlanDay,
} from "@/data/training-plan-generator";
import {
  getTrainingExperienceSettings,
  isHomeExercise,
  filterHomeExerciseIds,
  getHomeTrainingDay,
} from "@/data/training-environment";
import userStorage from "./user-storage";

export interface ExerciseConfiguration {
  sets: number;
  reps: string;
  restSeconds: number;
}
export interface MemberPlanDay extends ITrainingPlanDay {
  exerciseSettings?: Record<string, ExerciseConfiguration>;
  virtualId?: string;
}
export interface DailyTrainingOverride {
  id: string;
  date: string;
  kind: "same-plan" | "other-plan" | "free";
  originalDay: MemberPlanDay;
  day: MemberPlanDay;
}
export interface MemberTrainingPlan extends ITrainingPlan {
  scheduleMode?: "calendar";
  dailyOverrides?: Record<string, DailyTrainingOverride>;
}

export function localTrainingDate(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}
export function scheduledMemberDay(
  plan: MemberTrainingPlan,
  now = new Date(),
): MemberPlanDay | null {
  if (plan.scheduleMode === "calendar")
    return (
      plan.weekDays.find((d) => d.dayOfWeek === (now.getDay() || 7)) || null
    );
  const start = new Date(plan.startDate);
  const elapsed = Math.floor(
    (Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) -
      Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) /
      86400000,
  );
  return plan.weekDays[((elapsed % 7) + 7) % 7] || null;
}
export function memberDayId(
  plan: MemberTrainingPlan,
  day: MemberPlanDay,
): string {
  return day.virtualId || `member-${plan.id}-${day.dayOfWeek}`;
}
export function effectiveMemberDay(
  plan: MemberTrainingPlan,
  now = new Date(),
): MemberPlanDay | null {
  const override = plan.dailyOverrides?.[localTrainingDate(now)];
  const rawDay = override?.day || scheduledMemberDay(plan, now);
  const day = override?.kind === 'free' && rawDay ? normalizeFreeLibraryDay(rawDay) : rawDay;
  return day ? { ...day, courseDayId: memberDayId(plan, day) } : null;
}
export function readMemberPlan(): MemberTrainingPlan | null {
  try {
    return JSON.parse(userStorage.getItem("member_training_plan") || "null");
  } catch {
    return null;
  }
}

export function resolveMemberTrainingDay(
  id: string | undefined,
  plan = readMemberPlan(),
) {
  try {
    const active = JSON.parse(userStorage.getItem("active_workout") || "null");
    if (
      active?.active &&
      active.dayId === id &&
      active.executionSnapshot?.day?.id === id
    )
      return active.executionSnapshot as MemberTrainingSource;
  } catch {
    /* Fall back to the current plan. */
  }
  if (!id || !plan) return null;
  const override = Object.values(plan.dailyOverrides || {}).find(
    (o) => memberDayId(plan, o.day) === id,
  );
  const rawSelected: MemberPlanDay | undefined =
    override?.day || plan.weekDays.find((d) => memberDayId(plan, d) === id);
  const selected = override?.kind === 'free' && rawSelected ? normalizeFreeLibraryDay(rawSelected) : rawSelected;
  if (!selected || selected.type !== "strength") return null;
  const source = getDayById(selected.courseDayId || "");
  const course =
    MOCK_COURSES.find((c) => c.id === selected.courseId) ||
    source?.course ||
    MOCK_COURSES.find((c) => c.system === plan.courseSystem);
  if (!course) return null;
  const day: ICourseDay = {
    id,
    name: selected.label,
    dayIndex: selected.dayOfWeek,
    focus: selected.subtitle,
    durationMin: selected.durationMin,
    warmupIds: selected.warmupIds ?? source?.day.warmupIds ?? [],
    mainIds: selected.exerciseIds ?? source?.day.mainIds ?? [],
    stretchIds: selected.stretchIds ?? source?.day.stretchIds ?? [],
  };
  const settings = getTrainingExperienceSettings(plan.trainingMonths);
  const configurations: Record<string, ExerciseConfiguration> = {};
  for (const eid of day.mainIds) {
    const exercise = getExerciseById(eid);
    if (!exercise) continue;
    configurations[eid] =
      selected.exerciseSettings?.[eid] ||
      (override?.kind === "other-plan"
        ? {
            sets: exercise.sets,
            reps: exercise.reps,
            restSeconds: exercise.restSeconds,
          }
        : {
            sets: settings.sets,
            reps: /秒|分钟/.test(exercise.reps) ? exercise.reps : settings.reps,
            restSeconds: settings.restSec,
          });
  }
  return {
    course: {
      ...course,
      name: override?.kind === "free" ? "今日自由搭配" : course.name,
    },
    day,
    configurations,
    override,
    plan,
  };
}

export interface MemberTrainingSource {
  course: ICourse;
  day: ICourseDay;
  configurations: Record<string, ExerciseConfiguration>;
  override?: DailyTrainingOverride;
  plan: MemberTrainingPlan;
}

export function resolveTrainingDay(
  id: string | undefined,
  plan = readMemberPlan(),
) {
  return resolveMemberTrainingDay(id, plan) || (id ? getDayById(id) : null);
}
export function resolveMemberWorkoutDay(
  id: string,
  ids: string | null,
  ordered: boolean,
) {
  const source = resolveMemberTrainingDay(id);
  if (!source) return null;
  if (ids === null) return source;
  const allowed = [
    ...source.day.warmupIds,
    ...source.day.mainIds,
    ...source.day.stretchIds,
  ];
  const chosen = ids.split(",").filter((eid) => allowed.includes(eid));
  return {
    ...source,
    day: {
      ...source.day,
      warmupIds: ordered
        ? []
        : chosen.filter((eid) => source.day.warmupIds.includes(eid)),
      mainIds: ordered
        ? chosen
        : chosen.filter((eid) => source.day.mainIds.includes(eid)),
      stretchIds: ordered
        ? []
        : chosen.filter((eid) => source.day.stretchIds.includes(eid)),
      orderedIds: ordered ? chosen : undefined,
    },
  };
}
export function configurationQuery(
  ids: string[],
  configurations: Record<string, ExerciseConfiguration>,
): string {
  return ids
    .map((id) => {
      const ex = getExerciseById(id);
      const c = configurations[id] || {
        sets: ex?.sets || 1,
        reps: ex?.reps || "10",
        restSeconds: ex?.restSeconds || 60,
      };
      return `${c.sets}x${c.reps}x${c.restSeconds}`;
    })
    .join("|");
}
export function trainingRecordSource(id: string) {
  const resolved = resolveMemberTrainingDay(id);
  return resolved
    ? {
        memberPlanId: resolved.plan.id,
        sourceKind: resolved.override?.kind || "scheduled",
        replacedDayId: resolved.override
          ? memberDayId(resolved.plan, resolved.override.originalDay)
          : undefined,
        countsTowardPlan: resolved.override?.kind !== "free",
      }
    : {};
}

export const SCHEDULE_CHOICES = [
  ["chest", "胸"],
  ["back", "背"],
  ["legs", "腿"],
  ["shoulders", "肩"],
  ["arms", "手臂"],
  ["abs", "核心"],
  ["cardio", "有氧"],
  ["rest", "休息"],
] as const;
export type ScheduleChoice = (typeof SCHEDULE_CHOICES)[number][0];
export function buildScheduledDay(
  plan: MemberTrainingPlan,
  dayOfWeek: number,
  choice: ScheduleChoice,
): MemberPlanDay {
  if (choice === "rest")
    return {
      dayOfWeek,
      type: "rest",
      label: "休息日",
      subtitle: "完全休息",
      durationMin: 0,
    };
  if (choice === "cardio")
    return {
      dayOfWeek,
      type: "cardio",
      label: "有氧日",
      subtitle: "快走 / 慢跑 / 单车",
      cardioItem: "快走 / 慢跑 / 单车",
      durationMin: 30,
      intensity: "中",
    };
  const label = SCHEDULE_CHOICES.find((c) => c[0] === choice)![1];
  const pool = getAllExercises().filter(
    (e) =>
      e.bodyPart === choice &&
      !e.isWarmup &&
      !e.isStretch &&
      (plan.trainingPlace !== "home" || isHomeExercise(e)),
  );
  const preferred = plan.weekDays.find(
    (d) =>
      d.type === "strength" &&
      (d.subtitle.includes(label) || d.label.includes(label)),
  );
  const preferredIds = normalizeLibrarySelectionIds(preferred?.exerciseIds || []).filter((id) =>
    pool.some((e) => e.id === id),
  );
  const exerciseIds = [
    ...new Set([...preferredIds, ...pool.map((e) => e.id)]),
  ].slice(0, 4);
  return {
    dayOfWeek,
    type: "strength",
    label: `${label}日`,
    subtitle: `${label}部训练`,
    durationMin: plan.sessionMinutes || preferred?.durationMin || 45,
    intensity: getTrainingExperienceSettings(plan.trainingMonths).intensity,
    courseSystem: plan.courseSystem,
    courseId:
      preferred?.courseId || plan.weekDays.find((d) => d.courseId)?.courseId,
    exerciseIds,
    warmupIds: preferred?.warmupIds || [],
    stretchIds: preferred?.stretchIds || [],
  };
}
export function scheduleTemplate(
  plan: MemberTrainingPlan,
  count: 3 | 4 | 5,
): MemberPlanDay[] {
  const days =
    count === 3 ? [1, 3, 5] : count === 4 ? [1, 2, 4, 6] : [1, 2, 3, 5, 6];
  const choices: ScheduleChoice[] =
    count === 3
      ? ["chest", "back", "legs"]
      : count === 4
        ? ["chest", "back", "legs", "shoulders"]
        : ["chest", "back", "legs", "shoulders", "arms"];
  return Array.from({ length: 7 }, (_, i) =>
    buildScheduledDay(
      plan,
      i + 1,
      days.includes(i + 1) ? choices[days.indexOf(i + 1)] : "rest",
    ),
  );
}

export function courseDayAsMemberDay(
  course: ICourse,
  day: ICourseDay,
  dayOfWeek: number,
): MemberPlanDay {
  let execution = day;
  if (course.system === 'home') {
    const filtered = filterHomeExerciseIds(day.mainIds);
    execution = { ...day, mainIds: filtered.length >= 3 ? filtered : getHomeTrainingDay(2, '5-6', day.durationMin).day.mainIds,
      warmupIds: filterHomeExerciseIds(day.warmupIds), stretchIds: filterHomeExerciseIds(day.stretchIds) };
  }
  return {
    dayOfWeek,
    type: "strength",
    label: day.name,
    subtitle: course.name,
    durationMin: day.durationMin,
    courseSystem: course.system,
    courseId: course.id,
    courseDayId: day.id,
    exerciseIds: [...execution.mainIds],
    warmupIds: [...execution.warmupIds],
    stretchIds: [...execution.stretchIds],
  };
}
